import React, { useState, useEffect, useRef } from 'react';
import {
  Droplets, Lightbulb, Flame, Zap, Wifi, Trash2, ChevronRight,
  Camera, AlertTriangle, Image as ImageIcon,
  CheckCircle2, Fingerprint, Database, ShieldCheck,
  Activity, Globe, UploadCloud, Microscope,
  FileCheck2, Scale,
} from 'lucide-react';

import AssetPericialService from '../../services/AssetPericialService';
import { AuditoriaNormativaService } from '../../services/AuditoriaNormativaService';
import { laudoApi } from '../../services/laudoApi';
import { ETAPA, FATORES_CONFIG, proximaEtapa, LIMITES } from '../../constants/vistoria';

/**
 * ============================================================
 * COMPONENTE: VistoriaRegiaoFator01Servicos
 * PADRÃO: NBR 14653-2 | LUXURY UI (LEAD GRAY)
 * ------------------------------------------------------------
 * ✔ Fator canônico via constants/vistoria
 * ✔ Persistência via laudoApi (retry + fila offline)
 * ✔ Captura com hash SHA-256 + GPS real
 * ✔ Auditoria normativa real (sem mocks)
 * VERSÃO: 2.0.0
 * ============================================================
 */

const FATOR_ID = ETAPA.F01_SERVICOS;
const FATOR_CONFIG = FATORES_CONFIG[FATOR_ID];

/* =========================
   DICIONÁRIO ABNT — SERVIÇOS
========================= */

const DICIONARIO_ABNT = {
  aguaPotavel: [
    'REDE PÚBLICA INTEGRAL',
    'REDE PÚBLICA PARCIAL',
    'SISTEMA ALTERNATIVO REGULAR',
    'SISTEMA ALTERNATIVO PRECÁRIO',
    'INEXISTENTE',
  ],
  energiaEletrica: [
    'REDE PÚBLICA ESTÁVEL (MT/BT)',
    'REDE PÚBLICA COM OSCILAÇÃO',
    'REDE PARTICULAR / CONDOMINIAL',
    'SISTEMA ISOLADO (OFF-GRID)',
    'INEXISTENTE',
  ],
  esgotamentoSanitario: [
    'REDE PÚBLICA COLETORA',
    'SISTEMA DE FOSSA SÉPTICA (ABNT)',
    'FOSSA RUDIMENTAR',
    'LANÇAMENTO DIRETO',
    'INEXISTENTE',
  ],
  drenagemPluvial: [
    'GALERIAS E BUEIROS (COMPLETO)',
    'CANALIZAÇÃO ABERTA',
    'ESCOAMENTO POR GRAVIDADE (GUIA)',
    'AUSÊNCIA DE DRENAGEM (ALAGÁVEL)',
    'INEXISTENTE',
  ],
  gasCanalizado: [
    'REDE DE DISTRIBUIÇÃO URBANA',
    'CENTRAL DE GLP LOCAL',
    'ABASTECIMENTO INDIVIDUAL (BOTIJÃO)',
    'INEXISTENTE',
  ],
  telecomunicacoes: [
    'FIBRA ÓPTICA E 5G DISPONÍVEL',
    'CABO COAXIAL / DSL',
    'APENAS VIA SATÉLITE / RÁDIO',
    'SEM COBERTURA DE DADOS',
    'INEXISTENTE',
  ],
  residuosSolidos: [
    'COLETA DOMICILIAR REGULAR',
    'COLETA EM PONTO CENTRALIZADO',
    'SERVIÇO PARTICULAR CONTRATADO',
    'SEM SERVIÇO DE COLETA',
    'INEXISTENTE',
  ],
};

const PADRAO_SISTEMA = {
  aguaPotavel: 'REDE PÚBLICA INTEGRAL',
  energiaEletrica: 'REDE PÚBLICA ESTÁVEL (MT/BT)',
  esgotamentoSanitario: 'REDE PÚBLICA COLETORA',
  drenagemPluvial: 'GALERIAS E BUEIROS (COMPLETO)',
  gasCanalizado: 'REDE DE DISTRIBUIÇÃO URBANA',
  telecomunicacoes: 'FIBRA ÓPTICA E 5G DISPONÍVEL',
  residuosSolidos: 'COLETA DOMICILIAR REGULAR',
};

const ITENS_UI = [
  { id: 'aguaPotavel',          label: 'Abastecimento de Água Potável',      icon: Droplets },
  { id: 'energiaEletrica',      label: 'Energia Elétrica / Iluminação',      icon: Lightbulb },
  { id: 'esgotamentoSanitario', label: 'Esgotamento Sanitário (Rede)',       icon: Activity },
  { id: 'drenagemPluvial',      label: 'Drenagem Pluvial (Águas)',           icon: Globe },
  { id: 'gasCanalizado',        label: 'Distribuição de Gás',                icon: Flame },
  { id: 'telecomunicacoes',     label: 'Dados e Telecomunicações',           icon: Wifi },
  { id: 'residuosSolidos',      label: 'Coleta de Resíduos Sólidos',         icon: Trash2 },
];

/* =========================
   COMPONENTE
========================= */

export default function VistoriaRegiaoFator01Servicos({
  setNavegacao,
  infoLaudo,
  peritoAtivo,
}) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [activePhotoSlot, setActivePhotoSlot] = useState(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [loadingCaptura, setLoadingCaptura] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const [dadosInfra, setDadosInfra] = useState({
    ...PADRAO_SISTEMA,
    fatorValorizacao: '1.00',
    textoConsolidado: '',
    justificativaResumo: '',
    evidencias: {},
    justificativas: {},
  });

  /* ---------- divergências (quais campos fogem do padrão) ---------- */
  const divergencias = Object.keys(PADRAO_SISTEMA).filter(
    k => dadosInfra[k] !== PADRAO_SISTEMA[k]
  );

  /* ---------- auditoria normativa ---------- */
  const statusAuditoria = AuditoriaNormativaService.validarFinalizacao({
    ...dadosInfra,
    _divergencias: divergencias,
  });

  /* ---------- redação técnica automatizada ---------- */
  useEffect(() => {
    const desvio = AuditoriaNormativaService.calcularDesvio(dadosInfra.fatorValorizacao);
    const sinal = desvio >= 0 ? '+' : '';

    const base =
      `Para a determinação do valor de mercado, aplicou-se o **Fator de Localização Regional**, ` +
      `justificando-se a valoração de **${sinal}${desvio.toFixed(2)}%** em virtude da infraestrutura ` +
      `superior da zona avaliada. O cálculo seguiu estritamente a fórmula: ` +
      `$V_h = V_b \\times (1 + i)$ Onde **$V_h$** representa o valor homogeneizado e **$i$** o índice ` +
      `de valorização.`;

    const adendo = dadosInfra.justificativaResumo
      ? `\n\n**Fundamentação Técnica Adicional:** ${dadosInfra.justificativaResumo}`
      : '';

    const fechamento =
      `\n\nEsta metodologia encontra-se fundamentada na **NBR 14653-2:2011, Item ${FATOR_CONFIG.nbrItem}**, ` +
      `garantindo a precisão estatística do resultado final.`;

    setDadosInfra(prev => ({
      ...prev,
      textoConsolidado: base + adendo + fechamento,
    }));
  }, [dadosInfra.fatorValorizacao, dadosInfra.justificativaResumo]);

  /* ---------- captura com hash + GPS ---------- */
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activePhotoSlot) return;

    setErro(null);
    setLoadingCaptura(true);

    try {
      const resultado = await AssetPericialService.processarCaptura(
        file,
        FATOR_ID,
        infoLaudo?.numero || 'REPORT-AUTO',
        {
          perito: peritoAtivo,
          comodoLocal: activePhotoSlot,
          tentarGPS: true,
        }
      );

      setDadosInfra(prev => ({
        ...prev,
        evidencias: {
          ...prev.evidencias,
          [activePhotoSlot]: {
            url: resultado.url,
            hashIntegridade: resultado.hashIntegridade,
            fileName: resultado.fileName,
            metadados: resultado.metadados,
          },
        },
      }));

      setShowPhotoOptions(false);
    } catch (err) {
      setErro(err.message || 'Falha ao processar captura');
    } finally {
      setLoadingCaptura(false);
      e.target.value = null;
    }
  };

  /* ---------- persistência ---------- */
  const validarEGravar = async () => {
    if (statusAuditoria.isBloqueado) {
      setErro(`BLOQUEIO PERICIAL: ${statusAuditoria.mensagem}`);
      return;
    }

    setIsSaving(true);
    setErro(null);

    try {
      const pacoteFinal = {
        report_id: infoLaudo?.numero || 'LAUDO-PENDENTE',
        perito: peritoAtivo || 'MONTE_HERMOM_USER',
        fator: FATOR_ID,
        codigo: FATOR_CONFIG.codigo,
        pasta: FATOR_CONFIG.pasta,
        data_execucao: new Date().toISOString(),
        statusRevisao: 'PENDENTE',
        ...dadosInfra,
      };

      await laudoApi.salvarModulo(pacoteFinal);
      setNavegacao(proximaEtapa(FATOR_ID));
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — dados salvos localmente e serão sincronizados automaticamente.');
        // mesmo enfileirado, avança — o backend concilia depois
        setTimeout(() => setNavegacao(proximaEtapa(FATOR_ID)), 1200);
      } else {
        setErro(`Falha ao gravar: ${err.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isDivergente = (campo) => dadosInfra[campo] !== PADRAO_SISTEMA[campo];

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="max-w-4xl mx-auto space-y-12 italic font-black relative pb-20 uppercase">

      {/* MARCA D'ÁGUA DE FUNDO */}
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-[100px] z-0 select-none text-[#2F353B]">
        MONTE HERMOM
      </div>

      {/* INPUTS OCULTOS */}
      <input
        type="file"
        ref={cameraInputRef}
        onChange={onFileChange}
        accept="image/*"
        capture="environment"
        className="hidden"
      />
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        accept="image/*"
        className="hidden"
      />

      {/* HEADER */}
      <header className="flex justify-between items-end border-b-4 border-[#2F353B] pb-8 text-[#2F353B] relative z-10">
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-blue-600">
            <ShieldCheck size={22} className="animate-pulse" />
            <span className="text-[10px] tracking-[0.5em] text-slate-400 font-black">
              AUDITORIA DE COERÊNCIA ATIVA
            </span>
          </div>
          <h2 className="text-[20px] tracking-[0.2em] leading-none uppercase">
            {FATOR_CONFIG.titulo.split('. ')[1]?.split(' ')[0] || 'SERVIÇOS'}{' '}
            <span className="text-blue-600">E INFRAESTRUTURA</span>
          </h2>
        </div>
        <div className="bg-[#2F353B] px-12 py-6 rounded-[3rem] border-b-8 border-blue-600 shadow-2xl text-white text-[16px] font-black italic">
          RELATÓRIO: {infoLaudo?.numero || 'PENDENTE'}
        </div>
      </header>

      {/* ERRO */}
      {erro && (
        <div className="bg-red-900/90 text-white p-6 rounded-[2rem] flex items-center gap-6 relative z-10">
          <AlertTriangle size={24} />
          <span className="text-[11px] font-black italic tracking-widest">{erro}</span>
        </div>
      )}

      {/* LISTAGEM DE SERVIÇOS */}
      <section className="space-y-6 relative z-10">
        {ITENS_UI.map((item) => {
          const divergente = isDivergente(item.id);
          const temFoto = !!dadosInfra.evidencias[item.id];

          return (
            <div
              key={item.id}
              className={`group p-8 rounded-[4rem] border-2 transition-all duration-500 ${
                divergente
                  ? 'bg-blue-50 border-blue-600 shadow-xl'
                  : 'bg-white border-slate-100 hover:border-blue-200'
              }`}
            >
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-6 w-1/3 border-r-2 border-slate-100 pr-6">
                  <div
                    className={`p-5 rounded-[2.5rem] transition-transform group-hover:scale-110 ${
                      divergente
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-slate-50 text-blue-600'
                    }`}
                  >
                    <item.icon size={24} />
                  </div>
                  <label className="text-[11px] tracking-widest leading-tight font-black italic">
                    {item.label}
                  </label>
                </div>

                <div className="flex-1">
                  <select
                    value={dadosInfra[item.id]}
                    onChange={(e) =>
                      setDadosInfra(prev => ({ ...prev, [item.id]: e.target.value }))
                    }
                    className="w-full bg-transparent border-none text-slate-800 text-[12px] font-black italic outline-none cursor-pointer uppercase tracking-tight"
                  >
                    {DICIONARIO_ABNT[item.id].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="ml-6 flex items-center gap-4">
                  {divergente && !temFoto && (
                    <AlertTriangle className="text-amber-500 animate-bounce" size={20} />
                  )}
                  <button
                    onClick={() => {
                      setActivePhotoSlot(item.id);
                      setShowPhotoOptions(true);
                    }}
                    className={`p-6 rounded-full border-2 transition-all ${
                      temFoto
                        ? 'bg-blue-600 text-white shadow-xl'
                        : 'bg-white text-slate-200 hover:text-blue-600 hover:border-blue-600'
                    }`}
                  >
                    {temFoto ? <CheckCircle2 size={26} /> : <Camera size={26} />}
                  </button>
                </div>
              </div>

              {divergente && !temFoto && (
                <div className="mt-8 ml-20 animate-in slide-in-from-top-6 duration-700">
                  <textarea
                    value={dadosInfra.justificativas[item.id] || ''}
                    onChange={(e) =>
                      setDadosInfra(prev => ({
                        ...prev,
                        justificativas: {
                          ...prev.justificativas,
                          [item.id]: e.target.value,
                        },
                      }))
                    }
                    placeholder="Fundamentação técnica para ausência de evidência visual..."
                    className="w-full bg-white border-2 border-amber-200 rounded-[2.5rem] p-8 text-[12px] italic font-black text-slate-600 outline-none focus:border-amber-500"
                    rows={2}
                  />
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* PAINEL DE RESULTADO */}
      <section className="bg-[#2F353B] p-16 rounded-[5rem] shadow-2xl border-b-[20px] border-blue-600 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12 relative z-20">
          <div className="bg-[#1e293b] p-10 rounded-[4rem] text-center border border-white/5">
            <label className="text-[11px] text-blue-400 block mb-6 italic font-black uppercase tracking-[0.5em]">
              Fator Localização (i)
            </label>
            <div className="flex items-center justify-center gap-4">
              <Scale className="text-slate-500" size={24} />
              <input
                type="text"
                value={dadosInfra.fatorValorizacao}
                onChange={(e) =>
                  setDadosInfra(prev => ({ ...prev, fatorValorizacao: e.target.value }))
                }
                className="w-40 bg-transparent text-[32px] text-white italic outline-none text-center font-black border-b-4 border-blue-600"
              />
            </div>
          </div>

          <div className="bg-white/5 p-10 rounded-[4rem] flex flex-col items-center justify-center border border-white/10">
            <span className="text-white/40 text-[9px] mb-2 tracking-widest uppercase">
              Eficácia da Infraestrutura
            </span>
            <span
              className={`text-2xl font-black ${
                statusAuditoria.precisaJustificar
                  ? 'text-red-500 animate-pulse'
                  : 'text-green-500'
              }`}
            >
              {statusAuditoria.indicePercentual}%
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <Fingerprint
                className={statusAuditoria.precisaJustificar ? 'text-red-500' : 'text-blue-500'}
                size={28}
              />
              <span className="text-[11px] text-white tracking-[0.4em] font-black uppercase">
                Justificativa Técnica (Resumo)
              </span>
            </div>
          </div>

          <textarea
            value={dadosInfra.justificativaResumo}
            onChange={(e) =>
              setDadosInfra(prev => ({ ...prev, justificativaResumo: e.target.value }))
            }
            className={`w-full h-44 p-10 rounded-[4.5rem] bg-white text-slate-800 text-[13px] italic font-black outline-none border-8 transition-all ${
              statusAuditoria.precisaJustificar && !dadosInfra.justificativaResumo
                ? 'border-red-500'
                : 'border-transparent'
            }`}
            placeholder="O desvio estatístico exige fundamentação pericial..."
          />
        </div>

        <div className="mt-12 bg-white/5 p-12 rounded-[4.5rem] border border-white/5">
          <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-6">
            <FileCheck2 className="text-blue-600" size={24} />
            <span className="text-[10px] text-white/60 tracking-[0.3em] font-black uppercase">
              Prévia do Laudo [NBR 14653]
            </span>
          </div>
          <textarea
            readOnly
            value={dadosInfra.textoConsolidado}
            className="w-full h-40 bg-transparent border-none text-slate-300 text-[12px] leading-[1.8] font-black italic outline-none resize-none"
          />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pt-12 flex justify-between items-center border-t-2 border-slate-100 relative z-10">
        <button
          onClick={validarEGravar}
          disabled={isSaving}
          className="group flex items-center gap-16 bg-[#2F353B] text-white px-28 py-12 rounded-[6rem] shadow-3xl transition-all text-[20px] font-black italic hover:bg-blue-600 disabled:opacity-50"
        >
          {isSaving ? 'GRAVANDO...' : 'GRAVAR DADOS'}
          <ChevronRight
            size={36}
            className="group-hover:translate-x-6 transition-transform"
          />
        </button>
      </footer>

      {/* MODAL CAPTURA */}
      {showPhotoOptions && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-[#2F353B]/95 backdrop-blur-xl p-8">
          <div className="bg-white w-full max-w-lg rounded-[5rem] p-20 space-y-12 border-8 border-blue-600">
            <div className="text-center space-y-6">
              <div className="bg-blue-50 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-8 border-4 border-blue-100">
                <Microscope className="text-blue-600" size={48} />
              </div>
              <h3 className="text-[20px] font-black italic uppercase tracking-tighter text-[#2F353B]">
                Admissão de Evidência
              </h3>
              <span className="text-[9px] text-blue-600 font-black italic uppercase bg-blue-50 py-2 px-6 rounded-full inline-block">
                MÍNIMO {LIMITES.MEGAPIXELS_MIN}MP REQUERIDO
              </span>
            </div>

            <div className="flex flex-col gap-6">
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-[#2F353B] text-white p-12 rounded-[3.5rem] flex items-center justify-center gap-8 font-black italic hover:bg-blue-600 disabled:opacity-50"
              >
                <Camera size={28} /> {loadingCaptura ? 'VALIDANDO...' : 'DISPARAR CÂMERA'}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-slate-100 text-[#2F353B] border-4 border-slate-200 p-12 rounded-[3.5rem] flex items-center justify-center gap-8 font-black italic disabled:opacity-50"
              >
                <UploadCloud size={28} /> CARREGAR ARQUIVO
              </button>
            </div>

            <button
              onClick={() => {
                setShowPhotoOptions(false);
                setActivePhotoSlot(null);
              }}
              className="w-full text-[12px] text-slate-400 font-black italic uppercase tracking-[0.5em]"
            >
              ABORTAR OPERAÇÃO
            </button>
          </div>
        </div>
      )}
    </div>
  );
}